import dayjs from 'dayjs'
import { pixelDrawingBoundsSchema } from 'features/libra/drawing/drawingBounds'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { optionalOverrideJustificationSchema } from 'features/libra/overrides/overrideJustification'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useGeckoNotifications } from 'features/notifications'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { v4 } from 'uuid'
import { z } from 'zod'

// The NII strategy / justification / HOIS coverage logic lives in ./niiStrategy
// (the decision tree, its schemas, the modifier tables, and the coverage math);
// this file owns the zone SHAPE (zoneParamsSchema) and the ticket parsers/hooks.
// Consumers import NII symbols straight from ./niiStrategy — no barrel re-export
// (the repo's no-barrel-files rule forbids it, and it keeps deps explicit).
import {
  type ConsequenceOfFailure,
  consequenceOfFailureSchema,
  coverageCalculationSchema,
  niiStrategySchema,
  rbaInformationSchema,
} from './niiStrategy'

export const ZONE_ANALYSIS_STEP_COMPONENT = 'ZoneAnalysis'

export const ZONE_FIELD_IDS = {
  ZONES: 'zones',
  BREAKDOWN_JUSTIFICATION: 'zone_breakdown_justification',
  COF: 'consequence_of_failure',
} as const

const now = () => dayjs().format('YYYY-MM-DD')

/**
 * Zone editor tab keys, canonical source shared by the floating-card renderer
 * and the config schema. Ordered as the full workflow presents them. Host
 * steps may render a subset via `visibleTabs` — Data Gathering captures the
 * pre-screening tabs (`basic`/`design`/`damage`), while `strategy`/`rba`
 * depend on the screening `confidence` and stay in the later NII Zones step.
 */
export const ZONE_TAB_KEYS = [
  'basic',
  'design',
  'damage',
  'strategy',
  'rba',
] as const

export type ZoneTabKey = (typeof ZONE_TAB_KEYS)[number]

/** Mutable copy of {@link ZONE_TAB_KEYS} for use as a default prop value. */
export const ALL_ZONE_TAB_KEYS: ZoneTabKey[] = [...ZONE_TAB_KEYS]

export const damageMechanismSchema = z
  .object({
    id: z.string().default(() => v4().substring(0, 6)),
    name: z.string().optional().default(''),
    rate_value: z.number().optional(),
    rate_unit: z.string().optional().default('mm/y'),
    susceptibility: z.enum(['low', 'medium', 'high']).optional(),
    justification: z.string().optional(),
  })
  .loose()

// Per-tab AI prefill provenance. Keyed by the backend tab id (e.g.
// 'nii_strategy', 'risk_assessment'). `fields` are the AI-written fields the
// SME has NOT touched yet ("AI suggested until touched" — an edit removes the
// field from the list); `unfound` are fields the AI explicitly could not
// source, surfaced to the user rather than guessed.
export const zoneTabPrefillMetaSchema = z.object({
  generated_at: z.string().optional(),
  fields: z.array(z.string()).default([]),
  unfound: z.array(z.string()).default([]),
  rule_notes: z.array(z.string()).default([]),
  // Per-field citations ({doc, page, excerpt} or APM {doc, component,
  // field}) — same shape the Data Gathering AiCitationChip renders.
  citations: z.record(z.string(), z.unknown()).optional(),
  // Documents the gathering subagents actually drew on for this tab.
  documents_used: z.array(z.string()).optional(),
  component_match: z
    .object({
      component_entry_id: z.string().nullish(),
      component_name: z.string().nullish(),
      confidence: z.enum(['low', 'medium', 'high']).nullish(),
      justification: z.string().nullish(),
    })
    .nullish(),
})

export type ZoneTabPrefillMeta = z.infer<typeof zoneTabPrefillMetaSchema>

export const zoneRectangleSchema = pixelDrawingBoundsSchema.loose()

export type ZoneRectangle = z.infer<typeof zoneRectangleSchema>

const zoneRectanglesSchema = z
  .union([zoneRectangleSchema, z.array(zoneRectangleSchema)])
  .transform((bounds) => (Array.isArray(bounds) ? bounds : [bounds]))

export const zoneParamsSchema = z.object({
  id: z.string().default(() => v4().substring(0, 6)),
  title: z.string().default('Untitled Zone'),
  description: z.string().optional(),
  material: z.string().optional(),
  geometry: z.string().optional(),
  calculated_length_mm: z.number().optional(),
  length_mm_override: z.number().optional(),
  calculated_height_mm: z.number().optional(),
  height_mm_override: z.number().optional(),
  length_mm: z.number().optional(),
  height_mm: z.number().optional(),
  internal_diameter_mm: z.number().optional(),
  external_diameter_mm: z.number().optional(),
  total_surface_area_sq_m: z.number().optional(),

  // Surface-area calculator inputs (see ./zoneSurfaceArea). Diameter above and
  // the effective height are the other two; these four had nowhere to live
  // while the calculation happened in the SME's spreadsheet. Stored as plain
  // strings/numbers — `readZoneSurfaceAreaInputs` narrows the two option
  // fields, so an old or hand-edited value degrades to the default instead of
  // failing the whole zone's parse.
  length_basis: z.string().optional(),
  cylinder_proportion: z.number().optional(),
  head_type: z.string().optional(),
  head_proportion: z.number().optional(),
  /** Calculator output. Never AI-written: geometry is arithmetic, not a guess. */
  surface_area_calculated_sq_m: z.number().optional(),

  wall_thickness_shell_mm: z.number().optional(),
  wall_thickness_heads_mm: z.number().optional(),
  corrosion_allowance_mm: z.number().optional(),

  // Design & operating conditions. These live per-zone (not on the asset-level
  // Data Gathering step) because for many vessels they vary by region — e.g. an
  // exchanger's shell vs channel/tube sides carry different design pressures and
  // temperatures, and operating temperature differs across a liquid/vapour split.
  design_pressure_bar: z.number().optional(),
  operating_pressure_bar: z.number().optional(),
  design_temp_c: z.number().optional(),
  max_operating_temp_c: z.number().optional(),

  // Asset Model Reference (legacy/1% use case)
  componentId: z.string().optional(),
  bounds: z
    .object({
      x_start: z.number(),
      y_start: z.number(),
      x_end: z.number(),
      y_end: z.number(),
      unit: z.string(),
    })
    .optional(),

  // Zone Reference Image (primary/99% use case)
  zone_reference_image_id: z.string().optional(), // REPORT entry ID
  drawing_bounds: zoneRectanglesSchema.optional(),

  // AI suggestion review (POC): a zone proposed by the AI that the SME must
  // confirm / reject / edit. `ai_suggested` is true until confirmed.
  // `ai_initial_bounds` freezes the model's original boxes so they can be
  // compared against the SME-edited `drawing_bounds` (feedback signal).
  ai_suggested: z.boolean().optional(),
  ai_initial_bounds: zoneRectanglesSchema.optional(),
  ai_meta: z
    .object({
      model: z.string().optional(),
      generated_at: z.string().optional(),
    })
    .optional(),

  // Per-tab AI questionnaire prefill provenance (see zoneTabPrefillMetaSchema).
  ai_prefill: z.record(z.string(), zoneTabPrefillMetaSchema).optional(),

  calculated_area_sq_m: z.number().optional(),
  total_surface_area_sq_m_override: z.number().optional(),
  total_surface_area_sq_m_override_justification:
    optionalOverrideJustificationSchema,
  color: z.string().optional(),
  inspection_method: z.string().optional(),
  damage_mechanisms: z.array(damageMechanismSchema).optional().default([]),
  nii_strategy: niiStrategySchema.optional(),
  rba_info: rbaInformationSchema.optional(),
  coverage_calculation: coverageCalculationSchema.optional(),
  consequence_of_failure: z.number().optional(),
  zone_recommendations: z.array(z.string()).optional().default([]),
  assumptions: z.array(z.string()).optional().default([]),
  review_notes: z.string().optional(),
})

export const zoneAnalysisConfigSchema = z.object({
  zoneFieldName: z.string().default(ZONE_FIELD_IDS.ZONES),
  confidenceFieldName: z.string().optional(), // Field name for confidence from NII Screening
  // Optional subset and display order of zone editor tabs. When omitted, the
  // editor renders every available tab in its default order.
  visibleTabs: z.array(z.enum(ZONE_TAB_KEYS)).optional(),
  // Workflow-defined sections (the ZoneAnalysis step carries a section whose
  // `agent` block drives the backend zone-suggestion strategy). The frontend
  // only needs each section's `agentPermissions` — the per-section AI gating
  // every other agent step already honors — so the rest is ignored.
  sections: z
    .array(
      z.object({
        id: z.string(),
        agentPermissions: z.array(z.string()).optional(),
      })
    )
    .optional(),
})

export const zoneAnalysisDataSchema = z.object({
  zones: z.array(zoneParamsSchema).optional().default([]),
  analysisDate: z.string().default(now),
})

export type DamageMechanism = z.infer<typeof damageMechanismSchema>
export type ZoneParams = z.infer<typeof zoneParamsSchema>
export type ZoneBoxTarget = { zoneId: string; boxIndex: number }
export const zoneBoxLabel = (zoneTitle: string, boxIndex: number) =>
  `${zoneTitle} box ${boxIndex + 1}`
export const zoneBoxesToDocumentRegions = (zones: readonly ZoneParams[]) =>
  zones.flatMap((zone) =>
    (zone.drawing_bounds ?? []).map((drawing_bounds, boxIndex) => ({
      id: JSON.stringify([zone.id, boxIndex]),
      title: zoneBoxLabel(zone.title, boxIndex),
      color: zone.color,
      ai_suggested: zone.ai_suggested,
      zone_reference_image_id: zone.zone_reference_image_id,
      drawing_bounds,
      readOnly: true,
    }))
  )
export type ZoneChangeGuard = {
  zone_reference_image_id: ZoneParams['zone_reference_image_id']
  drawing_bounds: NonNullable<ZoneParams['drawing_bounds']>
}
export type ZoneAnalysisConfig = z.infer<typeof zoneAnalysisConfigSchema>
export type ZoneAnalysisData = z.infer<typeof zoneAnalysisDataSchema>

/**
 * Canonical Design & Function select options — the single source of truth for
 * both the `DesignFunctionPanel` dropdowns and the AI value canonicalization in
 * `zoneTabAutofill`, so an AI-normalized value can't drift from an offered one.
 */
export const MATERIAL_OPTIONS = [
  { value: 'CS', label: 'Carbon Steel (CS)' },
  { value: 'SS304', label: 'Stainless Steel 304' },
  { value: 'SS316', label: 'Stainless Steel 316' },
  { value: 'CRA', label: 'Corrosion Resistant Alloy (CRA)' },
  { value: 'CRA_CLAD', label: 'CRA Clad' },
  { value: 'DUPLEX', label: 'Duplex Stainless Steel' },
  { value: 'INCONEL', label: 'Inconel' },
  { value: 'MONEL', label: 'Monel' },
  { value: 'TITANIUM', label: 'Titanium' },
  { value: 'OTHER', label: 'Other' },
] as const

export const GEOMETRY_OPTIONS = [
  { value: 'RECTANGLE', label: 'Rectangle' },
  { value: 'CYLINDRICAL', label: 'Cylindrical' },
  { value: 'SPHERICAL', label: 'Spherical' },
  { value: 'CONICAL', label: 'Conical' },
  { value: 'ELLIPSOIDAL', label: 'Ellipsoidal' },
  { value: 'CUSTOM', label: 'Custom' },
] as const

// Zone geometry accessors. `getEffectiveZoneArea` lives in ./niiStrategy beside
// the coverage math that consumes it; length/height are pure geometry the zone
// panels read for display, so they stay here with the zone shape.
export function getEffectiveZoneLength(zone: ZoneParams): number | undefined {
  return zone.length_mm_override ?? zone.length_mm ?? zone.calculated_length_mm
}

export function getEffectiveZoneHeight(zone: ZoneParams): number | undefined {
  return zone.height_mm_override ?? zone.height_mm ?? zone.calculated_height_mm
}

const DEFAULT_COF: ConsequenceOfFailure = {
  level: 'medium' as const,
  justification: undefined,
}

const DEFAULT_SCREENING_CONFIDENCE = 'med' as const

const zoneArraySchema = z.array(zoneParamsSchema)

export interface StableZoneSnapshot {
  rawZones: unknown
  zones: ZoneParams[]
  parseError?: z.ZodError
  ticketId: string | undefined
  fieldName: string
  resetKey: number | undefined
}

export function useStableZoneSnapshot(
  fieldValues: FieldValues,
  fieldName: string,
  ticketId: string | undefined,
  baseline?: StableZoneSnapshot,
  resetKey?: number
): StableZoneSnapshot {
  const rawZones = fieldValues[fieldName]?.value
  const cached = useRef<StableZoneSnapshot | null>(null)

  const snapshot = useMemo(() => {
    const previous = cached.current
    const sameScope =
      previous !== null &&
      previous.ticketId === ticketId &&
      previous.fieldName === fieldName &&
      previous.resetKey === resetKey
    if (previous && sameScope && isEqual(previous.rawZones, rawZones)) {
      return previous
    }

    const parsed = parseZonesFromFieldValues(fieldValues, fieldName)
    const source =
      previous && sameScope
        ? previous
        : baseline &&
            baseline.ticketId === ticketId &&
            baseline.fieldName === fieldName &&
            baseline.resetKey === resetKey
          ? baseline
          : undefined
    const zones = source
      ? reconcileZoneSnapshot(
          source.rawZones,
          source.zones,
          rawZones,
          parsed.zones
        ).zones
      : parsed.zones
    return { ...parsed, rawZones, zones, ticketId, fieldName, resetKey }
  }, [baseline, fieldName, fieldValues, rawZones, resetKey, ticketId])

  useEffect(() => {
    cached.current = snapshot
  }, [fieldName, resetKey, snapshot, ticketId])

  return snapshot
}

/**
 * Pure parser for the zones field on a ticket. Returns the parsed zones plus
 * an optional `parseError` so callers can decide how to surface failures.
 * Returns `[]` when the field is absent.
 */
export function parseZonesFromFieldValues(
  fieldValues: FieldValues,
  fieldName: string = ZONE_FIELD_IDS.ZONES
): { zones: ZoneParams[]; parseError?: z.ZodError } {
  const rawZones = unwrapFieldValue<unknown>(fieldValues[fieldName])
  if (rawZones === undefined) return { zones: [] }
  const parsed = zoneArraySchema.safeParse(rawZones)
  if (!parsed.success) {
    return { zones: [], parseError: parsed.error }
  }
  return { zones: parsed.data }
}

function isZoneRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function reconcileZoneSnapshot(
  previousRawZones: unknown,
  previousZones: readonly ZoneParams[],
  nextRawZones: unknown,
  nextZones: readonly ZoneParams[]
): { zones: ZoneParams[]; hasAmbiguousLegacyChange: boolean } {
  const previousRaw = Array.isArray(previousRawZones) ? previousRawZones : []
  const nextRaw = Array.isArray(nextRawZones) ? nextRawZones : []
  const previousLegacy = previousRaw.filter(
    (zone): zone is Record<string, unknown> =>
      isZoneRecord(zone) && zone.id === undefined
  )
  const nextLegacy = nextRaw.filter(
    (zone): zone is Record<string, unknown> =>
      isZoneRecord(zone) && zone.id === undefined
  )
  const hasAmbiguousLegacyChange = [...previousLegacy, ...nextLegacy].some(
    (zone) =>
      previousLegacy.filter((candidate) => isEqual(candidate, zone)).length !==
        1 ||
      nextLegacy.filter((candidate) => isEqual(candidate, zone)).length !== 1
  )

  return {
    zones: nextZones.map((zone, index) => {
      const raw = nextRaw[index]
      if (!isZoneRecord(raw)) return zone

      if (raw.id === undefined) {
        const previousMatches = previousLegacy.filter((candidate) =>
          isEqual(candidate, raw)
        ).length
        const nextMatches = nextLegacy.filter((candidate) =>
          isEqual(candidate, raw)
        ).length
        if (previousMatches !== 1 || nextMatches !== 1) return zone
        const previousIndex = previousRaw.findIndex(
          (candidate) =>
            isZoneRecord(candidate) &&
            candidate.id === undefined &&
            isEqual(candidate, raw)
        )
        return previousZones[previousIndex] ?? zone
      }

      if (typeof raw.id !== 'string' || raw.id.length === 0) return zone
      const previousMatches = previousRaw.filter(
        (candidate) => isZoneRecord(candidate) && candidate.id === raw.id
      )
      const nextMatches = nextRaw.filter(
        (candidate) => isZoneRecord(candidate) && candidate.id === raw.id
      )
      if (
        previousMatches.length !== 1 ||
        nextMatches.length !== 1 ||
        !isEqual(previousMatches[0], raw)
      ) {
        return zone
      }
      const previousIndex = previousRaw.indexOf(previousMatches[0])
      return previousZones[previousIndex] ?? zone
    }),
    hasAmbiguousLegacyChange,
  }
}

/**
 * Pure parser for the Consequence-of-Failure field. Defaults to
 * `{ level: 'medium' }` when missing or invalid.
 */
export function parseCofFromFieldValues(fieldValues: FieldValues): {
  cof: ConsequenceOfFailure
  parseError?: z.ZodError
} {
  const rawCof = unwrapFieldValue<ConsequenceOfFailure>(
    fieldValues[ZONE_FIELD_IDS.COF]
  )
  if (!rawCof) return { cof: DEFAULT_COF }
  const parsed = consequenceOfFailureSchema.safeParse(rawCof)
  if (!parsed.success) {
    console.error('CoF parse error:', parsed.error)
    return { cof: DEFAULT_COF, parseError: parsed.error }
  }
  return { cof: parsed.data }
}

/**
 * Pure parser for the NII Screening confidence field. Defaults to `'med'`
 * when no `confidenceFieldName` is configured or the field is missing.
 */
export function parseConfidenceFromFieldValues(
  fieldValues: FieldValues,
  confidenceFieldName?: string
): 'low' | 'med' | 'high' {
  if (!confidenceFieldName) return DEFAULT_SCREENING_CONFIDENCE
  const rawValue = unwrapFieldValue<'low' | 'med' | 'high'>(
    fieldValues[confidenceFieldName]
  )
  return rawValue ?? DEFAULT_SCREENING_CONFIDENCE
}

export function useZoneData(fieldName: string = ZONE_FIELD_IDS.ZONES): {
  isLoading: boolean
  zones: ZoneParams[]
} {
  const { fieldValues, isLoading, ticketId } = useTicketAccess()
  const { errorNotification } = useGeckoNotifications()
  const parsed = useStableZoneSnapshot(fieldValues, fieldName, ticketId)
  const notifiedParse = useRef<ReturnType<
    typeof parseZonesFromFieldValues
  > | null>(null)

  useEffect(() => {
    if (!parsed.parseError) {
      notifiedParse.current = null
      return
    }
    if (notifiedParse.current === parsed) return

    notifiedParse.current = parsed
    errorNotification(`Invalid zone data in field: ${fieldName}`)
  }, [parsed, fieldName, errorNotification])

  return { isLoading, zones: parsed.zones }
}

export function useCofData(): {
  isLoading: boolean
  cof: ConsequenceOfFailure
} {
  const { fieldValues, isLoading } = useTicketAccess()
  const { errorNotification } = useGeckoNotifications()

  return useMemo(() => {
    const { cof, parseError } = parseCofFromFieldValues(fieldValues)
    if (parseError) {
      errorNotification('Invalid consequence of failure data')
    }
    return { isLoading, cof }
  }, [fieldValues, isLoading, errorNotification])
}

export function useZoneBreakdownJustificationData(): {
  isLoading: boolean
  zoneBreakdownJustification: string | undefined
} {
  const { fieldValues, isLoading } = useTicketAccess()
  return useMemo(() => {
    const raw = unwrapFieldValue<string>(
      fieldValues[ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]
    )
    return { isLoading, zoneBreakdownJustification: raw ?? undefined }
  }, [fieldValues, isLoading])
}

export function useSaveZoneBreakdownJustification(): (
  justification: string | undefined
) => Promise<void> {
  const { setFieldValues } = useTicketAccess()
  return useCallback(
    async (justification: string | undefined) => {
      await setFieldValues({
        zone_breakdown_justification: wrapFieldValue(justification ?? ''),
      })
    },
    [setFieldValues]
  )
}

/**
 * Hook to read confidence value from NII Screening step
 * Uses the confidenceFieldName from config to determine which field to read
 */
export function useConfidenceFromScreening(
  confidenceFieldName?: string
): 'low' | 'med' | 'high' {
  const { fieldValues } = useTicketAccess()
  return useMemo(
    () => parseConfidenceFromFieldValues(fieldValues, confidenceFieldName),
    [fieldValues, confidenceFieldName]
  )
}
